import React, { useState, useCallback, useMemo, useEffect } from 'react';
import { ChevronDown, ChevronRight, Zap, Settings } from 'lucide-react';
import { useComponentMetrics, getMetricLabel } from '../utils/metricsUtils';
import MetricsConfigModal from './MetricsConfigModal';
import ModuleVisibilityToggle from './ModuleVisibilityToggle';

/**
 * MatomoEventsTable - Affiche les événements Matomo avec hiérarchie
 * Catégorie > Action > Nom
 */
/*
 * ─── Les colonnes, décrites une fois ───────────────────────────────────────────────────────
 *
 * **Le tableau affichait deux colonnes en dur** — Événements et Visites — alors que le rapport
 * en rend davantage et que le composant proposait déjà une modale de configuration : son
 * `isMetricActive` n'était appelé nulle part, et choisir des métriques n'avait aucun effet.
 *
 * Les colonnes suivent désormais `componentMetrics.order`, comme partout ailleurs.
 */
const COLONNES = {
  events: { valeur: (l) => l.nb_events || 0, format: (v, f) => f(v), couleur: '#f59e0b', gras: '600' },
  visites: { valeur: (l) => l.nb_visits || 0, format: (v, f) => f(v) },
  visiteurs: { valeur: (l) => l.nb_uniq_visitors || 0, format: (v, f) => f(v) },
  actions_par_event: {
    valeur: (l) => ((l.nb_visits || 0) > 0 ? (l.nb_events || 0) / l.nb_visits : 0),
    format: (v, f) => f(v, 2)
  }
};

/** Dix catégories par page, comme tous les autres tableaux du tableau de bord. */
const ITEMS_PER_PAGE = 10;

const MatomoEventsTable = ({
  events = [],
  formatNumber,
  accountId = 'global',
  userRole = 'USER',
  isClientModuleVisible,
  toggleClientModuleVisibility,
  moduleId = 'matomo_events',
  visitesDuSite = 0,
  visiteursDuSite = 0
}) => {
  const [expandedCategories, setExpandedCategories] = useState({});
  const [expandedActions, setExpandedActions] = useState({});
  const [showMetricsConfig, setShowMetricsConfig] = useState(false);
  const [isVisible, setIsVisible] = useState(true);
  const [currentPage, setCurrentPage] = useState(1);

  // Hook pour les métriques du composant (réactif aux changements)
  const componentMetrics = useComponentMetrics('matomo_events', accountId);

  // Fonction helper pour vérifier si une métrique est sélectionnée
  const isMetricActive = useCallback((metricKey) => {
    return componentMetrics.metrics.includes(metricKey);
  }, [componentMetrics.metrics]);

  /** Les colonnes à rendre, dans l'ordre du modèle. */
  const colonnes = useMemo(() => {
    const ordre = componentMetrics.order?.length ? componentMetrics.order : componentMetrics.metrics;
    return (ordre || [])
      .filter((cle) => COLONNES[cle] && isMetricActive(cle))
      .map((cle) => ({ cle, ...COLONNES[cle] }));
  }, [componentMetrics.order, componentMetrics.metrics, isMetricActive]);


  /*
   * Dix catégories à l'écran, le reste à la page suivante.
   *
   * Vingt-deux catégories tenaient sur trois écrans, et la traîne — une catégorie à onze
   * événements — n'intéresse personne. Le compteur dit combien il en reste, de sorte qu'un
   * classement tronqué ne se prenne pas pour un classement complet. Même règle, même markup et
   * même libellé que Pages d'atterrissage.
   */
  const totalPages = Math.ceil((events?.length || 0) / ITEMS_PER_PAGE);
  const categoriesAffichees = useMemo(() => {
    const debut = (currentPage - 1) * ITEMS_PER_PAGE;
    return (events || []).slice(debut, debut + ITEMS_PER_PAGE);
  }, [events, currentPage]);

  // Un changement de période remet au début : rester en page 3 d'un autre classement n'a pas de sens.
  useEffect(() => { setCurrentPage(1); }, [events]);

  if (!events || events.length === 0) {
    return null;
  }

  const toggleCategory = (categoryLabel) => {
    setExpandedCategories(prev => ({
      ...prev,
      [categoryLabel]: !prev[categoryLabel]
    }));
  };

  const toggleAction = (categoryLabel, actionLabel) => {
    const key = `${categoryLabel}-${actionLabel}`;
    setExpandedActions(prev => ({
      ...prev,
      [key]: !prev[key]
    }));
  };

  // Les événements s'additionnent, les visites non.
  //
  // Chaque événement appartient à une seule catégorie : leur somme est le total du site.
  //
  // Une **visite**, elle, déclenche des événements dans plusieurs catégories, et Matomo la compte
  // dans chacune. Sommer la colonne donnait 32 566 visites là où le site en relevait 9 001 sur la
  // même semaine — un facteur 3,6, chaque visite étant comptée dans 3,6 catégories en moyenne.
  // C'est la même erreur que les visiteurs uniques des tableaux de répartition, et elle se répare
  // de la même façon : prendre le total du site, jamais la somme des lignes.
  //
  // Le nombre de visites ayant déclenché **au moins un** événement n'est pas calculable ici : il
  // faudrait l'union des visites des 22 catégories, que l'API ne rend pas. On affiche donc les
  // visites du site, cohérentes avec tous les autres composants, et rien du tout si elles
  // manquent — une somme fausse se lit comme un chiffre juste.
  const totalEvents = events.reduce((sum, cat) => sum + (cat.nb_events || 0), 0);

  /**
   * Le total d'une colonne — et le tiret quand il n'y en a pas.
   *
   * Ni les sessions ni les visiteurs ne s'additionnent : une session qui déclenche des
   * événements dans trois catégories figure dans les trois lignes. Les deux totaux viennent donc
   * du site — les sessions de la plage, et pour les visiteurs le cumul journalier, Matomo
   * refusant les uniques sur une plage. C'est la même valeur que le pied des tableaux de
   * répartition, et que la carte Utilisateurs de Résultats.
   */
  const totalColonne = (cle) => {
    if (cle === 'events') return formatNumber(totalEvents);
    if (cle === 'visites') return visitesDuSite > 0 ? formatNumber(visitesDuSite) : '-';
    if (cle === 'visiteurs') return visiteursDuSite > 0 ? formatNumber(visiteursDuSite) : '-';
    if (cle === 'actions_par_event') {
      return visitesDuSite > 0 ? formatNumber(totalEvents / visitesDuSite, 2) : '-';
    }
    return '-';
  };

  return (
    <>
      {/*
        L'en-tête est celui de la famille — `section-header`, un `h4`, l'icône et les deux
        boutons —, et non une carte à soi.

        Le composant s'enveloppait d'une carte blanche à ombre portée, avec sa propre barre crème
        et deux compteurs à droite. Posé au milieu de Pages d'atterrissage et d'Achats
        e-commerce, qui n'en ont pas, il se lisait comme un bloc étranger. Les deux totaux ont
        rejoint le pied du tableau, là où la famille met les siens.
      */}
      <div className="section-header">
        <h4 style={{ fontSize: '18px', fontWeight: '600' }}>
          <Zap size={18} style={{ marginRight: '8px', verticalAlign: 'middle' }} />
          Événements
        </h4>
        <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
          {userRole !== 'CLIENT' && (
            <button
              onClick={() => setShowMetricsConfig(true)}
              className="metrics-settings-btn"
              title="Configurer les métriques"
            >
              <Settings size={16} />
            </button>
          )}
          {toggleClientModuleVisibility ? (
            <ModuleVisibilityToggle
              isVisible={isClientModuleVisible ? isClientModuleVisible(moduleId) : true}
              onToggle={toggleClientModuleVisibility}
              userRole={userRole}
              moduleId={moduleId}
              title="Événements"
            />
          ) : (
            <button
              onClick={() => setIsVisible(!isVisible)}
              className="section-toggle-btn"
              title={isVisible ? 'Masquer la section' : 'Afficher la section'}
            >
              {isVisible ? '-' : '+'}
            </button>
          )}
        </div>
      </div>

      {isVisible && (
        <>
      {/*
        Un vrai `<table>`, et non une grille de `div`.
        ========================================

        La grille obligeait à redéclarer à la main ce que les autres tableaux obtiennent de la
        feuille commune : la taille de police, le retour à la ligne des en-têtes, la largeur des
        colonnes. Trois réglages posés un par un, et trois occasions de diverger — c'est
        exactement ce qui s'est produit, l'écran affichant trois tailles différentes.

        La classe est `devices-table`, **la même que Pages d'atterrissage** — et non un nom
        propre. Les règles de police et de décor sont écrites pour cette classe précise, pas pour
        la famille `table[class*="-table"]` qui ne porte que les largeurs : un nom à soi donnait
        bien des colonnes pleine largeur, mais une police de 16 px et des lignes sans séparateur.

        Les trois niveaux restent des lignes de ce même tableau, décalées par leur remplissage à
        gauche. Un tableau imbriqué aurait ses propres colonnes, et plus rien ne s'alignerait.
      */}
      <div className="device-table-wrapper" style={{ marginTop: '0', paddingTop: '0' }}>
        <div className="device-summary-table" style={{ marginTop: '0', paddingTop: '0' }}>
          <table className="devices-table" style={{ width: '100%', tableLayout: 'fixed', borderCollapse: 'collapse' }}>
          <thead>
            <tr>
              <th>Catégorie / Action / Nom</th>
              {colonnes.map((colonne) => (
                <th key={colonne.cle} className="numeric">
                  <span className="th-libelle">{getMetricLabel(colonne.cle, 'matomo_events', accountId)}</span>
                </th>
              ))}
            </tr>
          </thead>

          <tbody>
            {categoriesAffichees.map((category, catIndex) => {
              const isCatExpanded = expandedCategories[category.label];
              const hasActions = category.actions && category.actions.length > 0;

              return (
                <React.Fragment key={category.label || catIndex}>
                  <tr
                    onClick={() => hasActions && toggleCategory(category.label)}
                    className={hasActions ? 'expandable-row' : ''}
                    style={{ cursor: hasActions ? 'pointer' : 'default' }}
                  >
                    <td className="device-name">
                      <span style={{ display: 'inline-flex', alignItems: 'center', gap: '8px' }}>
                        {hasActions
                          ? (isCatExpanded
                            ? <ChevronDown size={16} style={{ color: '#9ca3af' }} />
                            : <ChevronRight size={16} style={{ color: '#9ca3af' }} />)
                          : <span style={{ width: '16px' }} />}
                        <strong style={{ color: '#1f2937' }}>{category.label}</strong>
                        {hasActions && (
                          <span style={{ fontSize: '0.85em', color: '#9ca3af' }}>
                            ({category.actions.length} action{category.actions.length > 1 ? 's' : ''})
                          </span>
                        )}
                      </span>
                    </td>
                    {colonnes.map((colonne) => (
                      <td
                        key={colonne.cle}
                        className="numeric"
                        style={{ fontWeight: colonne.gras || 'normal', color: colonne.couleur || '#374151' }}
                      >
                        {colonne.format(colonne.valeur(category), formatNumber)}
                      </td>
                    ))}
                  </tr>

                  {/* Niveau 2 : actions */}
                  {isCatExpanded && hasActions && category.actions.map((action, actIndex) => {
                    const actionKey = `${category.label}-${action.label}`;
                    const isActExpanded = expandedActions[actionKey];
                    const hasNames = action.names && action.names.length > 0;

                    return (
                      <React.Fragment key={action.label || actIndex}>
                        <tr
                          className="detail-row expandable-row"
                          onClick={() => toggleAction(category.label, action.label)}
                          style={{ cursor: 'pointer', backgroundColor: '#fafafa' }}
                        >
                          <td className="device-name" style={{ paddingLeft: '36px' }}>
                            <span style={{ display: 'inline-flex', alignItems: 'center', gap: '8px' }}>
                              {isActExpanded
                                ? <ChevronDown size={14} style={{ color: '#9ca3af' }} />
                                : <ChevronRight size={14} style={{ color: '#9ca3af' }} />}
                              <span style={{ fontWeight: '500', color: '#374151' }}>{action.label}</span>
                              {hasNames && (
                                <span style={{ fontSize: '0.85em', color: '#9ca3af' }}>
                                  ({action.names.length} nom{action.names.length > 1 ? 's' : ''})
                                </span>
                              )}
                            </span>
                          </td>
                          {colonnes.map((colonne) => (
                            <td
                              key={colonne.cle}
                              className="numeric"
                              style={{ color: colonne.couleur ? '#d97706' : '#6b7280' }}
                            >
                              {colonne.format(colonne.valeur(action), formatNumber)}
                            </td>
                          ))}
                        </tr>

                        {/* Niveau 3 : noms */}
                        {isActExpanded && (hasNames
                          ? action.names.map((name, nameIndex) => (
                            <tr key={name.label || nameIndex} className="detail-row" style={{ backgroundColor: '#f5f5f5' }}>
                              <td className="device-name" style={{ paddingLeft: '64px', color: '#6b7280' }}>
                                {name.label}
                              </td>
                              {colonnes.map((colonne) => (
                                <td
                                  key={colonne.cle}
                                  className="numeric"
                                  style={{ color: colonne.couleur ? '#92400e' : '#9ca3af' }}
                                >
                                  {colonne.format(colonne.valeur(name), formatNumber)}
                                </td>
                              ))}
                            </tr>
                          ))
                          : (
                            <tr className="detail-row" style={{ backgroundColor: '#f5f5f5' }}>
                              <td
                                colSpan={colonnes.length + 1}
                                style={{ paddingLeft: '64px', color: '#9ca3af', fontStyle: 'italic' }}
                              >
                                Aucun nom d'événement
                              </td>
                            </tr>
                          ))}
                      </React.Fragment>
                    );
                  })}
                </React.Fragment>
              );
            })}
          </tbody>

          <tfoot>
            <tr>
              <td className="device-name"><strong>Total</strong></td>
              {colonnes.map((colonne) => (
                <td key={colonne.cle} className="numeric"><strong>{totalColonne(colonne.cle)}</strong></td>
              ))}
            </tr>
          </tfoot>
          </table>

          {totalPages > 1 && (
            <div className="pagination-controls">
              <div className="pagination-info">
                Affichage {((currentPage - 1) * ITEMS_PER_PAGE) + 1}-{Math.min(currentPage * ITEMS_PER_PAGE, events.length)} sur {events.length} catégories
              </div>
              <div className="pagination-buttons">
                <button
                  onClick={() => setCurrentPage((page) => Math.max(1, page - 1))}
                  disabled={currentPage === 1}
                  className="pagination-btn"
                >
                  Precedent
                </button>
                <button
                  onClick={() => setCurrentPage((page) => Math.min(totalPages, page + 1))}
                  disabled={currentPage === totalPages}
                  className="pagination-btn"
                >
                  Suivant
                </button>
              </div>
            </div>
          )}
        </div>
      </div>
        </>
      )}

      {/* Modal de configuration des métriques */}
      <MetricsConfigModal
        isOpen={showMetricsConfig}
        onClose={() => setShowMetricsConfig(false)}
        componentId="matomo_events"
        accountId={accountId}
        componentTitle="Événements"
      />
    </>
  );
};

export default MatomoEventsTable;
